import { Form, Tooltip } from 'antd'
import { Rule } from 'antd/lib/form'
import HelpIcon from '../HelpIcon'
import { useEffect, useState } from 'react'
import './index.css'
import { CIMSFormItemProps } from './types'
import { parseDisabled } from '../../utils/parseDisabled'
import DefaultMessage from '../DefaultMessage'
import { an } from '@cims-online/shared'
import { getDeepestString } from '../../utils/getReactNodeLength'

/**
 * wraps a {@link Form.Item} for use in CIMS
 */
const CIMSFormItem: React.FC<CIMSFormItemProps> = ({
  help,
  label,
  children,
  requiredMessage,
  emailMessage,
  defaultMessage,
  invalid,
  customValidator,
  disabled,
  description,
  url = false,
  ...rest
}) => {
  const [rules, setRules] = useState<Rule[] | undefined>()
  let resolvedDefaultMessage
  if (defaultMessage)
    resolvedDefaultMessage =
      typeof defaultMessage === 'object'
        ? defaultMessage
        : { value: defaultMessage }
  useEffect(() => {
    const rules = Array<Rule>()
    if (!!requiredMessage)
      rules.push({
        required: true,
        message:
          typeof requiredMessage === 'string'
            ? requiredMessage
            : `Please enter ${
                label ? an(getDeepestString(label).toLowerCase()) : 'a value'
              }`,
      })
    if (!!emailMessage) rules.push({ type: 'email', message: emailMessage })
    if (invalid) {
      if (Array.isArray(invalid)) {
        invalid.forEach((item) =>
          rules.push({
            pattern: RegExp(item.testPattern),
            message: item.message,
          })
        )
      } else {
        rules.push({
          pattern: RegExp(invalid.testPattern),
          message: invalid.message,
        })
      }
    }
    if (url)
      rules.push({
        type: 'url',
        message:
          'Please enter a valid URL. It may need to be prefixed with http:// or https://',
      })
    if (customValidator) {
      const customValidators = Array.isArray(customValidator)
        ? customValidator
        : [customValidator]
      for (let cv of customValidators) {
        rules.push((formInstance) => ({
          validator: async (_, value) => {
            if (!cv.validator(value, formInstance)) throw new Error(cv.message)
          },
        }))
      }
    }
    rules.push({
      validateTrigger: 'onBlur',
      validator: async (_, value) => {
        if (typeof value === 'string' && !!value && !value.trim())
          throw new Error('Cannot contain only whitespace')
      },
    })
    setRules(!rules.length ? undefined : rules)
  }, [invalid, requiredMessage, emailMessage, url, customValidator, label])

  return (
    <Tooltip title={parseDisabled(disabled)}>
      <Form.Item
        {...rest}
        validateTrigger={['onChange', 'onBlur']}
        help={
          description ??
          (resolvedDefaultMessage ? (
            <DefaultMessage {...resolvedDefaultMessage} />
          ) : undefined)
        }
        label={<HelpIcon text={help}>{label}</HelpIcon>}
        /**
         * Just so you know, the index.css file for this component overwrites
         * the default styling associated with this prop
         */
        colon={!!label}
        rules={rules}
        normalize={(value) => (value === '' ? null : value)}
      >
        {children}
      </Form.Item>
    </Tooltip>
  )
}

export default CIMSFormItem
